import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, IconButton, Stack, styled, Typography } from '@mui/material';
import { Body2 } from 'components/BizlyOS/Typography/Typography';
import { useCallback, useState } from 'react';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';
import { EColors, getColor } from 'theme';
import { UploadContent } from './UploadContent';
import { getUploadStrategy, UPLOAD_PROVIDER, UploadProvider } from './uploadStrategies';

const DEFAULT_MAX_FILE_SIZE = 1024 * 1024 * 50; // 50MB

const UploadContainer = styled(Box, {
    shouldForwardProp: prop => prop !== 'isCenteredContent' && prop !== 'disabled' && prop !== 'error',
})<{ isCenteredContent?: boolean; disabled?: boolean; error?: boolean }>(
    ({ theme: { spacing }, isCenteredContent, disabled, error }) => ({
        width: '100%',
        minHeight: 91,
        borderRadius: '9px',
        ...(isCenteredContent
            ? {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
              }
            : { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }),
        backgroundColor: disabled ? getColor(EColors.v3Dark, 0.05) : getColor(EColors.v3LightGrayBackground),
        border: '1px dashed ' + getColor(EColors.v3Dark, 0.2),
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: spacing(3),
        opacity: disabled ? 0.6 : 1,
        '&:hover': {
            borderColor: disabled ? getColor(EColors.v3Dark, 0.2) : getColor(EColors.v3Dark),
        },
        ...(error && {
            borderColor: getColor(EColors.v3Error),
            backgroundColor: getColor(EColors.v3Error, 0.05),
            '&:hover': {
                borderColor: getColor(EColors.v3Error),
            },
        }),
        boxSizing: 'border-box',
    })
);

const FileContainer = styled(Box)(({ theme: { spacing } }) => ({
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.25),
    backgroundColor: getColor(EColors.pureWhite),
    border: '1px solid ' + getColor(EColors.v3Dark, 0.2),
    borderRadius: '9px',
    padding: spacing(0, 1.25),
}));

const FileName = styled(Body2)({
    flex: 1,
    '& > a': { color: getColor(EColors.pureBlack) },
});

const AttachIcon = styled(AttachFileIcon)({
    color: getColor(EColors.pureBlack),
});

const DeleteFileIcon = styled(DeleteOutlineIcon)({
    color: getColor(EColors.pureBlack),
});

export type UploadedFile = {
    id: string;
    url?: string;
    name: string;
    size?: number;
};

type FileInputFieldProps = {
    label: string;
    error?: boolean;
    directory: string;
    uploadProvider?: UploadProvider;
    maxFileLimit?: number;
    maxSize?: number;
    description?: string;
    required?: boolean;
    listingId?: string;
    disabled?: boolean;
    values: UploadedFile[];
    onChange?: (files: UploadedFile[]) => void;
    showFilesPreview?: boolean;
    dropzoneOptions?: Partial<DropzoneOptions>;
    isCenteredContent?: boolean;
    hintText?: string;
    helperText?: string;
};

export function FileUploadField({
    maxFileLimit = 6,
    maxSize = DEFAULT_MAX_FILE_SIZE,
    disabled = false,
    uploadProvider = UPLOAD_PROVIDER.BIZLY,
    description,
    hintText,
    helperText,
    error = false,
    showFilesPreview = true,
    values: files,
    directory,
    onChange: setFiles,
    dropzoneOptions,
    isCenteredContent = false,
}: FileInputFieldProps) {
    const [isUploading, setIsUploading] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    const uploadStrategy = getUploadStrategy(uploadProvider);

    const onDrop = useCallback(
        async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            if (disabled || isUploading) return;
            setLocalError(null);

            if (fileRejections.length > 0) {
                const sizeErrors = fileRejections.filter(rejection =>
                    rejection.errors.some(e => e.code === 'file-too-large')
                );
                if (sizeErrors.length > 0) {
                    setLocalError('File is larger than 50MB');
                    return;
                }
            }

            setIsUploading(true);

            const availableSlots = maxFileLimit - files.length;

            if (availableSlots <= 0) {
                setIsUploading(false);
                return;
            }

            const filesToUpload = acceptedFiles.slice(0, availableSlots);

            // Process files in parallel but preserve order
            Promise.all(
                filesToUpload.map(async file => {
                    try {
                        const uploadResult = await uploadStrategy.upload(file, directory);
                        return uploadResult
                            ? {
                                  id: crypto.randomUUID() as string,
                                  url: uploadResult.url as string,
                                  name: uploadResult.name,
                                  size: uploadResult.size,
                              }
                            : null;
                    } catch (error) {
                        console.error(error);
                        return null;
                    }
                })
            )
                .then(results => {
                    const newFiles = results.filter(Boolean) as UploadedFile[];
                    setFiles?.([...files, ...newFiles]);
                })
                .finally(() => setIsUploading(false));
        },
        [isUploading, disabled, maxFileLimit, files, directory, setFiles, uploadStrategy]
    );

    const defaultDropzoneOptions: DropzoneOptions = {
        onDrop,
        maxFiles: maxFileLimit,
        maxSize: maxSize,
        disabled: disabled || isUploading || files?.length >= maxFileLimit,
    };
    const mergedDropzoneOptions = { ...defaultDropzoneOptions, ...dropzoneOptions };

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone(mergedDropzoneOptions);

    const onDelete = (id: string) => {
        const newFiles = files.filter(file => file.id !== id);
        setFiles?.(newFiles);
    };

    const isDisabled = disabled || isUploading || files?.length >= maxFileLimit;

    return (
        <Stack gap={1} width="100%">
            <UploadContainer
                {...getRootProps()}
                isCenteredContent={isCenteredContent}
                disabled={isDisabled}
                error={error || !!localError}
            >
                <input {...getInputProps()} />
                <UploadContent
                    isDragActive={isDragActive}
                    isUploading={isUploading}
                    onUploadClick={isUploading ? undefined : open}
                    description={description}
                />
            </UploadContainer>
            {showFilesPreview &&
                files.map(file => (
                    <FileContainer key={file.id}>
                        <AttachIcon />
                        <FileName noWrap>
                            <a href={file.url} download>
                                {file.name}
                            </a>
                        </FileName>
                        {!disabled && (
                            <IconButton onClick={() => onDelete(file.id)} size="large">
                                <DeleteFileIcon />
                            </IconButton>
                        )}
                    </FileContainer>
                ))}
            <Typography variant="caption" color="text.secondary">
                {hintText}
            </Typography>
            {(helperText || localError) && (
                <Typography variant="caption" color="error">
                    {localError || helperText}
                </Typography>
            )}
        </Stack>
    );
}
